import { GA4RestClient } from './GA4RestClient';
import DeviceStorage from '../../../components/DeviceStorage/DeviceStorage';

/**
 * Transforma objetos dataLayer en formato GTM al payload que espera el
 * Measurement Protocol REST de GA4.
 *
 * Usado por: src/utils/trackers/GA4.ts
 *
 * Cubre tres patrones de payload:
 *
 * 1. Eventos planos:
 *    { event, user_id, country, content_name, ... }
 *
 * 2. Eventos ecommerce (wrapper ecommerce{}):
 *    { event, ecommerce: { items: [...], payment_type, value, ... } }
 *
 * 3. Evento 2FA (estructura híbrida con user_properties y events anidados):
 *    { event, user_properties: {...}, events: [{ timestamp_micros, params: {...} }] }
 */

/** Máximo engagement razonable por evento: 1 hora en ms */
const MAX_ENGAGEMENT_MS = 3600000;

export class DataLayerTransformer {

    /**
     * Transforma un objeto dataLayer al payload del Measurement Protocol REST de GA4.
     *
     * @param {object} dataLayer   Objeto recibido por el método send() de la clase GA4.
     * @param {string} fallbackUserId  user_id de instancia (this.user_id en GA4) usado
     *                                 como último fallback para client_id.
     * @returns {object} Payload listo para enviar con GA4RestClient.send().
     */
    static transform(dataLayer, fallbackUserId) {
        const {
            event: eventName,
            user_id,
            client_id: dataClientId,
            user_properties,
            events: nestedEvents,
            ecommerce,
            ...rest
        } = dataLayer;

        // Propiedades planas van directo a params.
        const params = { ...rest };

        // Patrón ecommerce: aplanamos el wrapper en params.
        if (ecommerce) {
            const { items, payment_type, value, transaction_id, cupon, ...ecommerceRest } = ecommerce;
            if (items)               params.items           = items;
            if (payment_type)        params.payment_type    = payment_type;
            if (value !== undefined) params.value           = value;
            if (transaction_id)      params.transaction_id  = transaction_id;
            if (cupon)               params.cupon           = cupon;
            Object.assign(params, ecommerceRest);
        }

        // Patrón 2FA: fusionamos params del evento anidado y rescatamos timestamp_micros.
        let timestamp_micros;
        if (nestedEvents && nestedEvents.length > 0) {
            const nestedEvent = nestedEvents[0];
            timestamp_micros = nestedEvent.timestamp_micros;
            if (nestedEvent.params) {
                Object.assign(params, nestedEvent.params);
            }
        }

        // engagement_time_msec debe ser un número entero entre 1 y MAX_ENGAGEMENT_MS.
        // Se descarta cualquier valor que sea un timestamp (> 1 hora) para evitar que
        // timestamp_micros del evento anidado se propague a este campo por error.
        const rawEngagement = params.engagement_time_msec;
        const parsedEngagement = typeof rawEngagement === 'number'
            ? rawEngagement
            : Number(rawEngagement);

        params.engagement_time_msec = (
            !rawEngagement ||
            isNaN(parsedEngagement) ||
            parsedEngagement <= 0 ||
            parsedEngagement > MAX_ENGAGEMENT_MS
        ) ? 1 : parsedEngagement;

        // session_id: debe ser número entero (requisito GA4 MP)
        params.session_id = GA4RestClient.getSessionId();

        const eventObj = { name: eventName, params };
        if (timestamp_micros) {
            eventObj.timestamp_micros = timestamp_micros;
        }

        // client_id: se delega a GA4RestClient para obtener el identificador
        // canónico. Se prioriza: dataLayer.client_id → DeviceStorage → fallbackUserId.
        // El string "anonymous" queda reservado como placeholder inválido.
        const clientId = GA4RestClient.getClientId(
            dataClientId || DeviceStorage.getItem('client_id') || fallbackUserId
        );

        const payload = {
            client_id: clientId,
            events: [eventObj]
        };

        if (user_id)         payload.user_id         = user_id;
        if (user_properties) payload.user_properties = user_properties;

        return payload;
    }
}



// WEBPACK FOOTER //
// ./src/utils/trackers/ga-api-rest-ps4/DataLayerTransformer.js